// INTERNENDSHARED: one shared, timezone-safe definition of "has this internship ended", used by
// both Dashboard.js (the Interns view badge/indicator) and EmployeeProfile.js (the "start
// offboarding" CTA), so a COO can never see the two screens disagree on the boundary day.
//
// internship_end_date is a DATE (YYYY-MM-DD), not a datetime, so the correct comparison is calendar
// date vs calendar date - never "convert the string to a Date at some midnight". The two previous
// implementations each converted to a Date: Dashboard used LOCAL midnight (new Date(str+'T00:00:00'))
// and EmployeeProfile used UTC midnight (new Date('YYYY-MM-DD')), so for a viewer west of UTC they
// disagreed on the end-date day itself. This matches the backend exactly, which compares the DATE
// column to date.today() (calendar date to calendar date, no timezone in play).
//
// Implementation: compare the two YYYY-MM-DD strings directly. Lexicographic order on a
// zero-padded YYYY-MM-DD string is identical to chronological order, so no Date object, no midnight,
// and no timezone ever enters the comparison. "Today" is the viewer's local calendar day, formatted
// as YYYY-MM-DD via local getFullYear/getMonth/getDate (NOT toISOString, which would shift to UTC
// and reintroduce the very bug this fixes).

/** The viewer's local calendar day as a YYYY-MM-DD string. */
export function localTodayYMD(now = new Date()) {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Normalize an internship_end_date value to a bare YYYY-MM-DD string, or null.
 *  Accepts "YYYY-MM-DD" or a fuller ISO string like "YYYY-MM-DDTHH:MM:SS..." (takes the date part). */
export function endDateYMD(value) {
  if (!value || typeof value !== 'string') return null;
  const ymd = value.slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(ymd) ? ymd : null;
}

/** True when internship_end_date is today or earlier, as calendar dates (timezone-independent).
 *  A missing/invalid date is never "ended". Matches the backend's internship_end_date <= today. */
export function isInternshipEnded(internshipEndDate, now = new Date()) {
  const end = endDateYMD(internshipEndDate);
  if (!end) return false;
  return end <= localTodayYMD(now);
}

/** Whole calendar days remaining until the end date (0 = ends today, negative = already ended),
 *  or null when there is no valid end date. Computed on the UTC-anchored date parts of both
 *  YYYY-MM-DD strings, so the day count itself is also timezone-independent (both sides use the
 *  same anchor, so the offset cancels). Used only for the "N days left" display, never for the
 *  ended/not-ended decision (that is isInternshipEnded above). */
export function internshipDaysRemaining(internshipEndDate, now = new Date()) {
  const end = endDateYMD(internshipEndDate);
  if (!end) return null;
  const today = localTodayYMD(now);
  const endUtc = Date.parse(`${end}T00:00:00Z`);
  const todayUtc = Date.parse(`${today}T00:00:00Z`);
  return Math.round((endUtc - todayUtc) / 86400000);
}
